/**
 * Analytics — Fraud API Contracts
 *
 * Wire format for FraudController and FraudRulesAdminController endpoints.
 */

import { z } from 'zod';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const FRAUD_ALERT_STATUSES = ['PENDING', 'REVIEWED_LEGITIMATE', 'REVIEWED_FRAUD'] as const;
export const FRAUD_RULE_TYPES = [
  'SAME_LOCATION_60S',
  'SCAN_VELOCITY_10HR',
  'COUNTER_REGRESSION',
  'SEQUENTIAL_IDS',
  'POST_EXIT_SCAN',
  'HMAC_FAILURES',
] as const;
export const FRAUD_RULE_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH'] as const;

// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------

export const ListFraudAlertsQuerySchema = z.object({
  status: z.enum(FRAUD_ALERT_STATUSES).optional(),
  wristbandId: z.string().optional(),
  dateFrom: z.string().regex(DATE_REGEX).optional(),
  dateTo: z.string().regex(DATE_REGEX).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type ListFraudAlertsQuery = z.infer<typeof ListFraudAlertsQuerySchema>;

export const ReviewFraudAlertRequestSchema = z.object({
  status: z.enum(['REVIEWED_LEGITIMATE', 'REVIEWED_FRAUD']),
  note: z.string().optional(),
});
export type ReviewFraudAlertRequest = z.infer<typeof ReviewFraudAlertRequestSchema>;

export const CreateFraudRuleRequestSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string(),
  ruleType: z.enum(FRAUD_RULE_TYPES),
  threshold: z.number().min(0),
  severity: z.enum(FRAUD_RULE_SEVERITIES),
  isEnabled: z.boolean().default(true),
  autoBlock: z.boolean().default(false),
});
export type CreateFraudRuleRequest = z.infer<typeof CreateFraudRuleRequestSchema>;

export const UpdateFraudRuleRequestSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().optional(),
  ruleType: z.enum(FRAUD_RULE_TYPES).optional(),
  threshold: z.number().min(0).optional(),
  severity: z.enum(FRAUD_RULE_SEVERITIES).optional(),
  isEnabled: z.boolean().optional(),
  autoBlock: z.boolean().optional(),
});
export type UpdateFraudRuleRequest = z.infer<typeof UpdateFraudRuleRequestSchema>;

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

export const AnalyticsFraudAlertSchema = z.object({
  id: z.string(),
  wristbandId: z.string().nullable().optional(),
  alertType: z.string(),
  status: z.enum(FRAUD_ALERT_STATUSES),
  reviewedBy: z.string().nullable().optional(),
  reviewedAt: z.string().or(z.date()).nullable().optional(),
  reviewNote: z.string().nullable().optional(),
  createdAt: z.string().or(z.date()),
});
export type AnalyticsFraudAlert = z.infer<typeof AnalyticsFraudAlertSchema>;

export const FraudAlertListResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(AnalyticsFraudAlertSchema),
  meta: z.object({
    page: z.number().int(),
    limit: z.number().int(),
    total: z.number().int(),
    totalPages: z.number().int(),
  }),
});
export type FraudAlertListResponse = z.infer<typeof FraudAlertListResponseSchema>;

export const FraudStatsDailyItemSchema = z.object({
  date: z.string().regex(DATE_REGEX),
  flagged: z.number().int(),
  fraud: z.number().int(),
  legitimate: z.number().int(),
});
export const FraudStatsResponseSchema = z.object({
  summary: z.object({
    totalFlagged: z.number().int(),
    confirmedFraud: z.number().int(),
    confirmedLegitimate: z.number().int(),
    pending: z.number().int(),
    falsePositiveRate: z.number().nullable(),
  }),
  daily: z.array(FraudStatsDailyItemSchema),
});
export type FraudStatsResponse = z.infer<typeof FraudStatsResponseSchema>;

export const FraudRuleSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  ruleType: z.string(),
  threshold: z.number(),
  severity: z.enum(FRAUD_RULE_SEVERITIES),
  isEnabled: z.boolean(),
  autoBlock: z.boolean(),
  createdAt: z.string().or(z.date()).optional(),
  updatedAt: z.string().or(z.date()).optional(),
});
export type FraudRule = z.infer<typeof FraudRuleSchema>;
